import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, Loader } from "lucide-react";

// Committee Grid Section
const CommitteeSection = ({ locationGroups, sortedLocations }) => (
  <div className="mt-4">
    <div className="space-y-8">
      {sortedLocations.map((location, idx) => {
        const people = locationGroups[location];
        if (!people?.length) return null;

        // Group by rank for rows
        const groupedByRank = people.reduce((acc, person) => {
          const rank = person.rank || 1;
          if (!acc[rank]) acc[rank] = [];
          acc[rank].push(person);
          return acc;
        }, {});

        const sortedRanks = Object.keys(groupedByRank).sort((a, b) => a - b);

        return (
          <div
            key={idx}
            className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden"
          >
            {sortedLocations.length > 1 && (
              <div className="px-4 md:px-6 pt-4 pb-2 bg-gray-50">
                <h3 className="text-lg md:text-xl font-bold text-gray-800">
                  {location}
                </h3>
              </div>
            )}

            <div className="p-4 md:p-6 space-y-8">
              {sortedRanks.map((rank) => (
                <div key={rank} className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                  {groupedByRank[rank].map((person, i) => (
                    <div
                      key={i}
                      className="flex flex-col items-center w-full max-w-[280px] mx-auto mb-4"
                    >
                      <div className="relative w-full aspect-[4/5] mb-4">
                        <img
                          src={person.image_url}
                          alt={person.name}
                          className="w-full h-full object-cover rounded-[32px] shadow-2xl transition-transform duration-300 hover:scale-[1.02]"
                          loading="lazy"
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/300x400?text=CRC+Bharat";
                          }}
                        />
                      </div>

                      <div className="w-full flex flex-col items-center text-center">
                        <h3 className="font-bold text-gray-900 text-base md:text-lg mb-1">
                          {person.name}
                        </h3>
                        {person.position && person.position.toLowerCase() !== "null" && (
                          <div className="text-blue-700 font-medium text-xs md:text-sm mb-2 uppercase tracking-wide">
                            {person.position}
                          </div>
                        )}
                        {person.description && person.description.toLowerCase() !== "null" && (
                          <p className="text-xs md:text-sm text-gray-600 leading-relaxed px-2 line-clamp-6">
                            {person.description}
                          </p>
                        )}
                        {person.state && person.state.toLowerCase() !== "null" && (!person.description || person.description.toLowerCase() === "null") && (
                          <div className="text-gray-600 text-xs mt-1">
                            {person.state}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// Pyramid Layout (Hierarchical Tree)
const PyramidCommitteeSection = ({ locationGroups, sortedLocations }) => (
  <div className="mt-4">
    <div className="space-y-8">
      {sortedLocations.map((location, idx) => {
        const people = locationGroups[location];
        if (!people?.length) return null;

        // Group by rank for hierarchical rows
        const groupedByRank = people.reduce((acc, person) => {
          const rank = person.rank || 1;
          if (!acc[rank]) acc[rank] = [];
          acc[rank].push(person);
          return acc;
        }, {});

        const sortedRanks = Object.keys(groupedByRank).sort((a, b) => a - b);

        return (
          <div
            key={idx}
            className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden"
          >
            {sortedLocations.length > 1 && (
              <div className="px-4 md:px-6 pt-4 pb-2 bg-gray-50">
                <h3 className="text-lg md:text-xl font-bold text-gray-800">
                  {location}
                </h3>
              </div>
            )}

            <div className="p-4 md:p-6 flex flex-col items-center space-y-10 w-full">
              {sortedRanks.map((rank) => (
                <div key={rank} className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-8 md:gap-12 w-full">
                  {groupedByRank[rank].map((person, pi) => (
                    <div
                      key={pi}
                      className="flex flex-col items-center w-full max-w-[280px] mx-auto mb-4"
                    >
                      <div className="relative w-full aspect-[4/5] mb-4">
                        <img
                          src={person.image_url}
                          alt={person.name}
                          className="w-full h-full object-cover rounded-[32px] shadow-2xl transition-transform duration-300 hover:scale-[1.02]"
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/300x400?text=CRC+Bharat";
                          }}
                        />
                      </div>

                      <div className="w-full flex flex-col items-center text-center">
                        <h3 className="font-bold text-gray-900 text-base md:text-lg mb-1">
                          {person.name}
                        </h3>
                        {person.position && person.position.toLowerCase() !== "null" && (
                          <div className="text-blue-700 font-medium text-xs md:text-sm mb-2 uppercase tracking-wide">
                            {person.position}
                          </div>
                        )}
                        {person.description && person.description.toLowerCase() !== "null" && (
                          <p className="text-xs md:text-sm text-gray-600 leading-relaxed px-2 line-clamp-6">
                            {person.description}
                          </p>
                        )}
                        {person.state && person.state.toLowerCase() !== "null" && (!person.description || person.description.toLowerCase() === "null") && (
                          <div className="text-gray-600 text-xs mt-1">
                            {person.state}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// Tab Button
const CommitteeTab = ({ title, index, isActive, onClick }) => {
  const isBlueBg = index % 2 === 0;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 md:px-6 md:py-4 rounded-xl border-2 transition-all duration-300 flex justify-between items-center ${
        isActive
          ? "bg-blue-700 text-white border-blue-700 font-bold shadow-lg"
          : isBlueBg
            ? "bg-blue-700 text-white border-blue-600 hover:bg-blue-800 hover:shadow-md"
            : "bg-white text-black border-gray-200 hover:bg-gray-50 hover:shadow-md"
      }`}
    >
      <span className="text-sm md:text-base font-medium">{title}</span>
      {isActive ? <ChevronUp /> : <ChevronDown />}
    </button>
  );
};

export default function Patrons() {
  const [activeTabs, setActiveTabs] = useState(["patrons"]);
  const [committeeData, setCommitteeData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCommitteeData();
  }, []);

  const fetchCommitteeData = async () => {
    try {
      const response = await fetch("https://crcbharat.com/apis/committee.php");
      if (!response.ok) throw new Error("Failed to fetch");
      const result = await response.json();
      
      // Access the .data array from the response object
      if (result.status === "success" && Array.isArray(result.data)) {
        setCommitteeData(result.data);
      } else {
        console.error("Invalid API response format:", result);
        setCommitteeData([]);
      }
    } catch (error) {
      console.error("Error fetching committee data:", error);
      setCommitteeData([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleTab = (id) =>
    setActiveTabs((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    );

  const groupByRoleAndState = (data) => {
    const roles = {
      patrons: { title: "Honorable National Patron Members", groups: {} },
      national: { title: "National Committee", groups: {}, pyramid: true },
      state: { title: "State Committee", groups: {} },
      social: { title: "Social Justice Wing", groups: {} },
      law: { title: "Law & Justice Wing", groups: {} },
      advisory: { title: "Advisory & Research Committee", groups: {} },
      medical: { title: "Medical & Health Wing", groups: {} },
      it: { title: "IT Wing", groups: {} },
      youth: { title: "Youth Wing", groups: {} },
    };

    const roleMap = {
      "Honorable National Patron Members": "patrons",
      "National Committee": "national",
      "State Committee": "state",
      "Social Justice Wing": "social",
      "Law & Justice Wing": "law",
      "Advisory & Research Committee": "advisory",
      "Medical & Health Wing": "medical",
      "IT Wing": "it",
      "Youth Wing": "youth",
    };

    data.forEach((person) => {
      const roleKey = roleMap[person.role];
      if (roleKey && roles[roleKey]) {
        const state = person.state || "National";
        if (!roles[roleKey].groups[state]) {
          roles[roleKey].groups[state] = [];
        }
        roles[roleKey].groups[state].push(person);
      }
    });

    return Object.keys(roles).map((key) => ({
      id: key,
      ...roles[key],
    }));
  };

  const committees = groupByRoleAndState(committeeData);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-10 h-10 text-blue-700 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-10 px-4">
      <div className="max-w-7xl mx-auto space-y-4">
        {committees.map((c, i) => {
          const sortedLocations = Object.keys(c.groups).sort();
          if (sortedLocations.length === 0) return null;

          return (
            <div key={c.id}>
              <CommitteeTab
                title={c.title}
                index={i}
                isActive={activeTabs.includes(c.id)}
                onClick={() => toggleTab(c.id)}
              />
              {activeTabs.includes(c.id) && (
                <div className="animate-fadeIn px-2 md:px-4">
                  {c.pyramid ? (
                    <PyramidCommitteeSection
                      locationGroups={c.groups}
                      sortedLocations={sortedLocations}
                    />
                  ) : (
                    <CommitteeSection
                      locationGroups={c.groups}
                      sortedLocations={sortedLocations}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

